// Landing screen — convert visitor → started conversation.
// Mobile-first; the iOS frame is 402×874 — content fits above the fold and
// the rest reveals on scroll. Off-white paper, serif headline, single rame CTA.
//
// Italian microcopy preserved VERBATIM from
// handoff_input/design/screens/landing.jsx (per spec §7 hard constraint —
// "DO NOT swap the Italian microcopy for English or refine it").

import {
  BustPlaceholder,
  Button,
  IconButton,
  Surface,
  TopBar,
  Wordmark,
} from "@/design-system";
import { useBranding } from "@/lib/branding";
import { useScreenTitle } from "@/lib/use-screen-title";

export interface LandingProps {
  onStart: () => void;
  onLogin?: () => void;
}

const HOW_ROWS = [
  {
    n: "01",
    t: "Una conversazione",
    d: "MIMIR ti pone qualche domanda. Tre, quattro minuti.",
  },
  {
    n: "02",
    t: "Un'analisi del tuo profilo",
    d: "Età, abitudini, obiettivi, eventuali farmaci.",
  },
  {
    n: "03",
    t: "Una scelta personalizzata",
    d: "Solo gli integratori giusti per te. Niente di più.",
  },
] as const;

const FOOTER_LINKS = ["Privacy", "Termini", "Cookie", "Contatti"] as const;

export function Landing({ onStart, onLogin }: LandingProps) {
  useScreenTitle("L'integrazione, considerata");
  const { displayName } = useBranding();
  const isICS = !displayName || displayName === "InCorpoSano";
  return (
    <Surface>
      <TopBar
        left={<Wordmark size={16} sub displayName={displayName} />}
        right={
          onLogin ? (
            <button
              type="button"
              onClick={onLogin}
              className="font-sans text-[13.5px] text-ink-muted hover:text-ink bg-transparent border-none cursor-pointer px-2 py-1"
            >
              Accedi
            </button>
          ) : (
            <IconButton ariaLabel="Menu">
              <svg
                width="18"
                height="14"
                viewBox="0 0 18 14"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              >
                <path d="M2 2h14M2 7h14M2 12h10" />
              </svg>
            </IconButton>
          )
        }
      />

      {/* ─── Above the fold ─────────────────────────────────── */}
      <section className="px-6 pt-7 pb-5">
        <p className="font-mono text-[11px] uppercase tracking-[1.4px] text-ink-faint mb-[22px]">
          MENS SANA · IN CORPORE SANO
        </p>

        <h1 className="font-serif text-[44px] leading-[1.04] tracking-[-0.6px] text-ink m-0 font-medium">
          L'integrazione,
          <br />
          <em className="font-medium italic">considerata.</em>
        </h1>

        <p className="text-[17px] leading-[1.55] text-ink-muted mt-[22px] mb-[28px] max-w-[320px]">
          Un assistente che ascolta, capisce il tuo profilo e propone solo
          ciò che è giusto per te. Senza supposizioni.
        </p>

        <Button variant="primary" full onClick={onStart}>
          Inizia ora
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M2 7h10M8 3l4 4-4 4" />
          </svg>
        </Button>
        {onLogin && (
          <p className="mt-3 text-center text-[13px] text-ink-muted">
            Hai già un account?{" "}
            <button
              type="button"
              onClick={onLogin}
              className="border-b border-dotted border-ink-faint bg-transparent border-x-0 border-t-0 cursor-pointer text-ink"
            >
              Accedi
            </button>
          </p>
        )}

        <div className="mt-[18px] flex items-center gap-2.5 text-[13px] text-ink-faint">
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            className="text-sage-success"
            aria-hidden="true"
          >
            <path d="M2 6.5l2.5 2.5L10 3.5" />
          </svg>
          <span>Sviluppato con farmacisti esperti in fitoterapia</span>
        </div>
      </section>

      {/* ─── Anchor image (Roman bust placeholder) ───────────── */}
      <section className="px-6 pt-2 pb-7">
        <div className="rounded-[4px] bg-paper-muted pt-[18px] pb-1.5">
          <BustPlaceholder height={220} />
        </div>
      </section>

      {/* ─── Below the fold — three tiles ────────────────────── */}
      <section className="px-6 pt-6 pb-2">
        <p className="font-mono text-[11px] uppercase tracking-[1.2px] text-ink-faint mb-[18px]">
          Come funziona
        </p>

        {HOW_ROWS.map((row, i, arr) => (
          <div
            key={row.n}
            className={
              "flex gap-4 py-[18px] " +
              (i < arr.length - 1 ? "border-b border-[rgba(60,50,40,0.1)]" : "")
            }
          >
            <span className="font-mono text-xs text-rame pt-[3px] shrink-0">
              {row.n}
            </span>
            <div>
              <div className="font-serif text-[19px] text-ink mb-1">
                {row.t}
              </div>
              <div className="text-[14.5px] text-ink-muted leading-[1.5]">
                {row.d}
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* ─── Safety governance — quiet trust marker ──────────── */}
      <section className="px-6 pt-7 pb-6">
        <div className="bg-paper-card rounded-card p-[22px] shadow-card flex gap-3.5 items-start">
          <div className="w-8 h-8 rounded-lg bg-paper-muted shrink-0 flex items-center justify-center">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              className="text-ink"
              aria-hidden="true"
            >
              <path d="M8 1.5l5.5 2.2v3.8c0 3-2.4 5.7-5.5 7-3.1-1.3-5.5-4-5.5-7V3.7L8 1.5z" />
              <path d="M5.5 8l1.8 1.8L10.8 6" />
            </svg>
          </div>
          <div className="flex-1">
            <div className="text-[14px] font-medium text-ink mb-1">
              Sicurezza prima di tutto
            </div>
            <div className="text-[13.5px] text-ink-muted leading-[1.5]">
              Ogni consiglio è verificato contro oltre 150 interazioni
              farmacologiche e controindicazioni cliniche.
            </div>
          </div>
        </div>
      </section>

      {/* ─── Footer ──────────────────────────────────────────── */}
      <footer className="px-6 pt-5 pb-10 border-t border-[rgba(60,50,40,0.08)] mt-2">
        <Wordmark size={14} displayName={displayName} />
        {isICS && (
          <div className="font-serif italic text-sm text-ink-faint mt-1.5">
            in corpore sano
          </div>
        )}

        <div className="flex flex-wrap gap-x-[18px] gap-y-2.5 mt-[18px] text-[12.5px] text-ink-muted">
          {FOOTER_LINKS.map((label) => (
            <span
              key={label}
              className="border-b border-dotted border-ink-faint cursor-pointer"
            >
              {label}
            </span>
          ))}
        </div>

        <p className="text-[11px] text-ink-faint mt-[18px] leading-[1.5]">
          Gli integratori non sostituiscono una dieta varia ed equilibrata
          e uno stile di vita sano. Consulta il medico in caso di dubbi.
        </p>
      </footer>
    </Surface>
  );
}
